import {
  add,
  differenceInSeconds,
  formatDuration,
  intervalToDuration,
  isValid,
  parseISO,
} from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { isNil } from 'lodash';

const MIN_SLEEP_SECONDS = 1;
const durationTokenPattern =
  /(\d+(?:\.\d+)?)\s*(years?|yrs?|y|months?|mos?|mo|weeks?|w|days?|d|hours?|hrs?|h|minutes?|mins?|m|seconds?|secs?|s|milliseconds?|msecs?|ms)(?![a-z])/gi;
const isoDurationPattern =
  /^P(?=.*\d)(?:(\d+(?:\.\d+)?)Y)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i;

export type SleepDurationInput = string | number;

export type SleepUntilInput = {
  timestamp: string;
  timezone?: string;
};

type ParsedDuration = {
  seconds?: number;
  minutes?: number;
  hours?: number;
  days?: number;
  weeks?: number;
  months?: number;
  years?: number;
};

export function formatSecondsAsDuration(
  seconds: number,
  now = new Date()
): string {
  const duration = intervalToDuration({
    start: now,
    end: add(now, { seconds }),
  });
  return formatDuration(duration) || `${seconds} seconds`;
}

export function formatSleepDescription(args: {
  for?: SleepDurationInput;
  until?: SleepUntilInput;
}): string | undefined {
  try {
    if (args.until?.timestamp) {
      const wakeUpAt = parseWakeUpTimestamp(args.until);
      const displayTimezone = args.until.timezone ?? 'UTC';
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeZone: displayTimezone,
      }).format(wakeUpAt);
      const formattedTime = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: displayTimezone,
        timeZoneName: 'short',
      }).format(wakeUpAt);
      return `Sleeping until ${formattedTime} on ${formattedDate}`;
    }
    if (!isNil(args.for)) {
      const seconds = secondsFromDuration(args.for);
      return `Sleeping for ${formatSecondsAsDuration(seconds)}`;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

export function secondsUntilSleepEnds(
  until: SleepUntilInput | undefined,
  duration: SleepDurationInput | undefined,
  now = new Date()
): number {
  if (!isNil(until)) return secondsFromNowToTimestamp(until, now);
  if (!isNil(duration)) return secondsFromDuration(duration, now);
  throw new Error('Provide exactly one of `for` or `until`.');
}

export function secondsFromNowToTimestamp(
  until: SleepUntilInput,
  now = new Date()
): number {
  const wakeUpAt = parseWakeUpTimestamp(until);
  const seconds = differenceInSeconds(wakeUpAt, now);
  return requireValidSleepSeconds(seconds);
}

export function secondsFromDuration(
  duration: SleepDurationInput,
  now = new Date()
): number {
  if (typeof duration === 'number') {
    return requireValidSleepSeconds(Math.floor(duration));
  }

  const trimmedDuration = duration.trim();
  if (!trimmedDuration) throw new Error('Sleep duration must not be empty.');

  const parsedDuration =
    durationFromIsoDuration(trimmedDuration) ??
    durationFromNaturalLanguage(trimmedDuration);
  if (!parsedDuration) throw new Error(`Invalid sleep duration: ${duration}`);

  const wakeUpAt = add(now, parsedDuration);
  return requireValidSleepSeconds(differenceInSeconds(wakeUpAt, now));
}

function durationFromIsoDuration(duration: string): ParsedDuration | null {
  const match = duration.match(isoDurationPattern);
  if (!match) return null;

  const [
    ,
    years = '0',
    months = '0',
    weeks = '0',
    days = '0',
    hours = '0',
    minutes = '0',
    seconds = '0',
  ] = match;
  return {
    days: Number(days),
    hours: Number(hours),
    minutes: Number(minutes),
    months: Number(months),
    seconds: Number(seconds),
    weeks: Number(weeks),
    years: Number(years),
  };
}

function durationFromNaturalLanguage(duration: string): ParsedDuration | null {
  const parsedDuration: ParsedDuration = {};
  let matchedText = '';

  for (const match of Array.from(duration.matchAll(durationTokenPattern))) {
    const [, amount, unit] = match;
    addDurationAmount(parsedDuration, Number(amount), unit.toLowerCase());
    matchedText += match[0];
  }
  if (!matchedText) return null;

  const unmatchedText = duration
    .replace(durationTokenPattern, '')
    .replace(/[\s,]/g, '')
    .replace(/and/gi, '');
  return unmatchedText ? null : parsedDuration;
}

function addDurationAmount(
  duration: ParsedDuration,
  amount: number,
  unit: string
) {
  if (['millisecond', 'milliseconds', 'msec', 'msecs', 'ms'].includes(unit)) {
    duration.seconds = (duration.seconds ?? 0) + amount / 1000;
  } else if (['second', 'seconds', 'sec', 'secs', 's'].includes(unit)) {
    duration.seconds = (duration.seconds ?? 0) + amount;
  } else if (['minute', 'minutes', 'min', 'mins', 'm'].includes(unit)) {
    duration.minutes = (duration.minutes ?? 0) + amount;
  } else if (['hour', 'hours', 'hr', 'hrs', 'h'].includes(unit)) {
    duration.hours = (duration.hours ?? 0) + amount;
  } else if (['day', 'days', 'd'].includes(unit)) {
    duration.days = (duration.days ?? 0) + amount;
  } else if (['week', 'weeks', 'w'].includes(unit)) {
    duration.weeks = (duration.weeks ?? 0) + amount;
  } else if (['month', 'months', 'mo', 'mos'].includes(unit)) {
    duration.months = (duration.months ?? 0) + amount;
  } else if (['year', 'years', 'yr', 'yrs', 'y'].includes(unit)) {
    duration.years = (duration.years ?? 0) + amount;
  }
}

function parseWakeUpTimestamp({ timestamp, timezone }: SleepUntilInput): Date {
  const parsedDate = parseISO(timestamp);
  if (!isValid(parsedDate)) {
    throw new Error(`Invalid 'until' timestamp: ${timestamp}`);
  }
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(timestamp)) return parsedDate;

  const timestampTimezone = timezone ?? 'UTC';
  if (!isValidTimezone(timestampTimezone)) {
    throw new Error(`Invalid 'until' timezone: ${timestampTimezone}`);
  }
  const zonedDate = fromZonedTime(timestamp, timestampTimezone);
  if (!isValid(zonedDate)) {
    throw new Error(`Invalid 'until' timestamp: ${timestamp}`);
  }
  return zonedDate;
}

function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

function requireValidSleepSeconds(seconds: number): number {
  if (seconds < MIN_SLEEP_SECONDS) {
    throw new Error('Sleep duration must be at least 1 second.');
  }
  return Math.floor(seconds);
}
